import { Inject, Injectable, LOCALE_ID } from '@angular/core';

/**
 * Thin wrapper around Angular's LOCALE_ID injection so components can ask
 * "is current locale English?" without repeatedly injecting LOCALE_ID.
 *
 * Locale codes match angular.json i18n block:
 *   - zh-Hant  (default, source locale)
 *   - en
 */
@Injectable({ providedIn: 'root' })
export class LocaleService {
  constructor(@Inject(LOCALE_ID) private readonly locale: string) {}

  /** Raw locale id — e.g. 'zh-Hant' or 'en'. */
  get id(): string {
    return this.locale;
  }

  /** True when the current locale bundle is English. */
  get isEn(): boolean {
    return this.locale?.toLowerCase().startsWith('en');
  }

  /** True when the current locale bundle is Chinese. */
  get isZh(): boolean {
    return !this.isEn;
  }

  /** URL prefix for the current locale — '/en/' or '/zh/'. */
  get baseHref(): string {
    return this.isEn ? '/en/' : '/zh/';
  }

  /**
   * Compute the equivalent URL in the opposite locale, given the current
   * pathname (from window.location). Used by the language switcher.
   */
  swapLocaleUrl(currentPathname: string, currentHash: string): string {
    const otherPrefix = this.isEn ? '/zh/' : '/en/';
    // Strip current /en/ or /zh/ prefix, keep everything after.
    const stripped = currentPathname.replace(/^\/(en|zh)(\/|$)/, '/');
    return otherPrefix.replace(/\/$/, '') + stripped + (currentHash || '');
  }
}
